import { lazy, Suspense } from 'react';
import { Outlet, Navigate, useRoutes, RouteObject } from 'react-router';

import Box from '@mui/material/Box';

import HomeLayout from 'src/layouts/home/layout';
import { useStore } from 'src/store/StoreContext';
import { Loader } from 'src/components/loading';
import {DashboardLayout} from 'src/layouts/dashboard';
import { observer } from 'mobx-react-lite';
import { PatientProvider } from 'src/store/userContext';
import { PatientHistory } from 'src/models/patient-history';
import Dashboard from 'src/pages/admin/dashboard';
import ForgotPassword from 'src/pages/forgot-password';
import ResetPassword from 'src/pages/reset-password';
import Contact from 'src/sections/home/Contact';
import PatientQuery from 'src/sections/patient/patient_query';
import AboutUs from 'src/sections/home/AboutUs';
import DoctorIntroPage from 'src/sections/auth/doc-intro';
import { DoctorInfoPage } from 'src/sections/auth/doctor-interest-expression';
import ComingSoon from 'src/sections/withoutLayout/ComingSoon';
import PrivacyPolicy from 'src/commingSoon/components/PrivacyPolicy';
import TermsAndConditions from 'src/commingSoon/components/TermsAndConditions';


export const HomePage = lazy(() => import('src/pages/home'));
export const SignInPage = lazy(() => import('src/pages/sign-in'));
export const RegisterPage = lazy(() => import('src/pages/register'));
export const Page404 = lazy(() => import('src/pages/page-not-found'));
export const RegisterationConfirmation = lazy(() => import('src/pages/registeration-confirmation')); 
export const ServiceCreation = lazy(() => import('src/pages/service')); 

export const Carousels = lazy(() => import('src/pages/admin/carousels')); 
export const FAQ = lazy(() => import('src/pages/admin/faq')); 
export const Partners = lazy(() => import('src/pages/admin/partners')); 
export const Testimonials = lazy(() => import('src/pages/admin/testimonials')); 
export const AdminServices = lazy(() => import('src/pages/admin/services')); 
export const AdminServicesCreate = lazy(() => import('src/pages/admin/create-service'));
export const AdminServicesUpdate = lazy(() => import('src/pages/admin/update-service')); 
export const AdminDashboard = lazy(() => import('src/pages/admin/dashboard')); 
export const AdminReport = lazy(() => import('src/pages/admin/adminReport')); 
export const AdminDoctors = lazy(() => import('src/pages/admin/doctor'));

export const DoctorDashboard = lazy(() => import('src/pages/doctor/dashboard'));
export const AHPRAAUpdate = lazy(() => import('src/pages/doctor/ahpra'));
export const DoctorCalendar = lazy(() => import('src/pages/doctor/calendar'));
export const DoctorProfile = lazy(() => import('src/pages/doctor/profile'));
export const DoctorAvailability = lazy(() => import('src/pages/doctor/availability'));
export const DoctorReport= lazy(() => import('src/pages/doctor/report'));
export const PatientHistorys= lazy(() => import('src/pages/doctor/patient-history'));
export const RaiseQuery= lazy(() => import('src/pages/doctor/raise-query'));

export const DoctorConsultation = lazy(() => import('src/pages/doctor/consultation'));

export const PatientService = lazy(() => import('src/pages/patient/services'));

export const PatientServiceList = lazy(() => import('src/pages/patient/service-list'));
export const PatientDashboard = lazy(() => import('src/pages/patient/dashboard'));
export const FamilyMembers = lazy(() => import('src/pages/patient/members'));

export const SuperAdminRoles = lazy(() => import('src/pages/superadmin/roles')); 
export const SuperAdminPatients = lazy(() => import('src/pages/superadmin/patient'));
export const SuperAdminDoctors = lazy(() => import('src/pages/superadmin/doctor'));
export const SuperAdminDoctorDetails = lazy(() => import('src/pages/superadmin/doctorDetail'));
export const AdminDoctorDetails = lazy(() => import('src/pages/admin/doctorDetail'));
export const SuperAdminPatientDetails = lazy(() => import('src/pages/superadmin/patientDetails'));
export const LogMaintenance = lazy(() => import('src/pages/superadmin/logMaintenance'));

const renderFallback = (
  <Box display="flex" alignItems="center" justifyContent="center" flex="1 1 auto">
      <Loader/>
  </Box>
);

const ProtectedLayout = observer(({ allowedRoles }: { allowedRoles: string[] }) => {
  const user = useStore()?.user;
  if (!user?.user) {
    return <Navigate to="/" replace />;
  }
  const normalizedAllowedRoles = allowedRoles.map(role => role.toLowerCase());
  const userRoles = user?.user?.role?.toLowerCase();
  if (!userRoles || !normalizedAllowedRoles.includes(userRoles)) {
    return <Navigate to="/404" replace />;
  }

  return <DashboardLayout >
    {suspenseLayout()}
  </DashboardLayout>;
});

const AuthenticationLayout = observer(() => {
  const user = useStore()?.user;
  if (!user?.user) {
    return suspenseLayout();
  }
  return <Navigate to={`/${user.user?.role.toLowerCase()}`} replace />;  
});


let publicRoutes: Array<RouteObject> = [
  { path: 'home', element: <HomePage /> },
  { path: 'Service/:id', element: <ServiceCreation />},
  { path: 'contact', element: <Contact  /> },
   { path: 'AboutUs', element: <AboutUs  /> },
    { path:'doc-intro', element: <DoctorIntroPage/> },
     { path:'doctor-interset-expression', element: <DoctorInfoPage/> }
];

let authorizationRoutes: Array<RouteObject> = [
  { path: 'login', element: <SignInPage /> },
  { path: 'password', element: <ForgotPassword /> },
  { path: 'resetpassword', element: <ResetPassword /> },
  { path: 'register', children: [
    { path: 'doctor', element: <RegisterPage  /> },
    { path: 'patient', element: <RegisterPage isPatient={true} /> },
    { path: 'confirmation/:id', element: <RegisterationConfirmation  /> },
    
  ]},
];

let adminRoutes: Array<RouteObject> = [
  { element: <AdminDashboard />, index: true },
  { path: 'dashboard', element: <Dashboard /> },
  { path: 'doctors', element: <AdminDoctors /> },
  { path: 'doctors/:id', element: <AdminDoctorDetails /> },
  { path: 'carousels', element: <Carousels /> },
  { path: 'partners', element: <Partners /> },
  { path: 'testimonials', element: <Testimonials /> },
  { path: 'faqs', element: <FAQ /> },
  { path: 'adminReport', element: <AdminReport /> },
  {
    path:'services', children:[
          {
            path:'', element:<AdminServices/>
          },
          {
            path:':id', element:<AdminServices/>
          },
          {
            path:'create', element:<AdminServicesCreate/>
          },
          {
            path:'update/:id', element:<AdminServicesUpdate/>
          }
    ]
  },
];

let patientRoutes: Array<RouteObject> = [
  { element: <PatientDashboard />, index: true },
  {path:'Services', element: <PatientServiceList/>},
  {path:'Service/:id', element: <PatientService/>},
  {path:'members', element: <FamilyMembers/>},
  {path:'patient_query', element: <PatientQuery/>},
];

let doctorRoutes: Array<RouteObject> = [
  { element: <DoctorDashboard />, index: true },
  { path: 'consult/:id', element: <DoctorConsultation /> },
  { path: 'profile', element: <DoctorProfile /> },
  { path: 'availability', element: <DoctorAvailability /> },
  { path: 'calendar', element: <DoctorCalendar /> },
  { path: 'report/:id', element: <DoctorReport /> },
  { path: 'patient-history/:userId', element: <PatientHistorys /> },
  { path: 'AHPRA', element: <AHPRAAUpdate /> },
  { path: 'raise-query', element: <RaiseQuery /> }
];

let superAdminRoutes: Array<RouteObject> = [
   ...adminRoutes,
   { path: 'roles', element: <SuperAdminRoles /> },
   { path: 'doctors', element: <SuperAdminDoctors /> },
   { path: 'doctors/:id', element: <SuperAdminDoctorDetails /> },
   { path: 'patients', element: <SuperAdminPatients /> },
   { path: 'patients/:id', element: <SuperAdminPatientDetails /> },
   { path: 'logMaintenance', element: <LogMaintenance/> },
];

const suspenseLayout =() =>  <Suspense fallback={renderFallback}>
<Outlet />
</Suspense>;

export function Router() {
  return useRoutes([
    {
      path: '/',
      element: <ComingSoon />,
    },
    {
      path: '/privacy-policy',
      element: <PrivacyPolicy />,
    },
    {
      path: '/terms-and-conditions',
      element: <TermsAndConditions />,
    },
    {
      element: (
        <HomeLayout>
          {suspenseLayout()}
          </HomeLayout>
      ),
      children: publicRoutes,
    },
    {
      element: <AuthenticationLayout  />,
      children: [
        {
          element:  <HomeLayout>
          {suspenseLayout()}
          </HomeLayout>,
          children: authorizationRoutes,
        }
      ]
    },
    {
      path: 'doctor',
      element: <ProtectedLayout allowedRoles={['doctor']} />,
      children: [
        {
          element: suspenseLayout(),
          children: doctorRoutes,
        }
      ]
    },
    {
      path: 'superAdmin',
      element: <ProtectedLayout allowedRoles={['superadmin']} />,
      children: [
        {
          element: suspenseLayout(),
          children: superAdminRoutes,
        }
      ]
    },
    {
      path: 'patient',
      element: <PatientProvider> <ProtectedLayout allowedRoles={['patient']} /></PatientProvider>,
      children: [{
        element: suspenseLayout(),
        children: patientRoutes,
      }],
    },
    {
      path: 'admin',
      element: <ProtectedLayout allowedRoles={['admin']} />,
      children: [{
        element: suspenseLayout(),
        children: adminRoutes,
      }],
    },
    {
      path: '404',
      element: <Page404 />,
    },
    {
      path: '*',
      element: <Navigate to="/404" replace />,
    },
  ]);
}